/**
 * CryptoContext - Manages encryption key lifecycle
 *
 * Architecture:
 * - Master key is random (generated at signup, never derived from password)
 * - Master key is "wrapped" by password-derived key and recovery key
 * - On login: fetch wrapped key from server, unwrap with password
 * - On signup: generate random master key, wrap both ways, store on server
 * - On recovery: unwrap with recovery key, re-wrap with new password
 *
 * Provides:
 * - Master key state management
 * - Unlock (unwrap master key from server using password)
 * - Lock (clear keys on logout)
 * - Encrypt/decrypt functions
 * - Session persistence via localStorage
 */
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import {
  deriveKeys,
  encrypt,
  decrypt,
  encryptObject,
  decryptObject,
  storeMasterKey,
  retrieveMasterKey,
  clearMasterKey,
  hasMasterKey,
  derivePasswordKey,
  unwrapMasterKeyWithPassword,
} from '../lib/crypto';

export interface CryptoContextType {
  /** Whether the encryption vault is unlocked (master key available) */
  isUnlocked: boolean;
  /** Whether we're currently loading the master key from storage */
  isLoading: boolean;
  /** 
   * Unlock the vault by deriving keys from password.
   * For existing users: fetches wrapped key from server and unwraps.
   * For new users (signup): use setMasterKey directly after generating.
   * 
   * @param password - User's password
   * @param email - User's email
   * @param accessToken - Optional access token for fetching wrapped key
   * @returns Derived keys (authKey for Supabase, masterKey for encryption)
   */
  unlock: (
    password: string,
    email: string,
    accessToken?: string
  ) => Promise<{ authKey: string; masterKey: Uint8Array }>;
  /** 
   * Set the master key directly (used during signup or recovery).
   * Also stores the key in session storage.
   */
  setMasterKey: (masterKey: Uint8Array) => Promise<void>;
  /** Lock the vault and clear stored keys */
  lock: () => void;
  /** Encrypt a string */
  encrypt: (plaintext: string) => Promise<string>;
  /** Decrypt a string */
  decrypt: (ciphertext: string) => Promise<string>;
  /** Encrypt an object (JSON stringified) */
  encryptObject: <T>(obj: T) => Promise<string>;
  /** Decrypt an object (JSON parsed) */
  decryptObject: <T>(ciphertext: string) => Promise<T>;
  /** Get the raw master key (for wrapping operations) */
  getMasterKey: () => Uint8Array | null;
}

export const CryptoContext = createContext<CryptoContextType | null>(null);

interface CryptoProviderProps {
  children: ReactNode;
}

export function CryptoProvider({ children }: CryptoProviderProps) {
  const [masterKey, setMasterKeyState] = useState<Uint8Array | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount, try to restore master key from storage
  useEffect(() => {
    const restoreKey = async () => {
      try {
        if (await hasMasterKey()) {
          const key = await retrieveMasterKey();
          if (key) {
            setMasterKeyState(key);
          }
        }
      } catch (error) {
        console.warn('Failed to restore master key:', error);
      } finally {
        setIsLoading(false);
      }
    };
    restoreKey();
  }, []);

  // Unlock: derive password key, fetch and unwrap master key from server
  const unlockVault = useCallback(
    async (password: string, email: string, accessToken?: string) => {
      // Always derive authKey for Supabase (still needed for auth)
      const keys = await deriveKeys(password, email);

      // If we have an access token, try to fetch wrapped master key from server
      if (accessToken) {
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/api/recovery-keys/me`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            if (data.wrapped_by_password) {
              // Unwrap master key using password-derived key
              const passwordKey = await derivePasswordKey(password, email);
              const unwrappedMasterKey = await unwrapMasterKeyWithPassword(
                data.wrapped_by_password,
                passwordKey
              );
              setMasterKeyState(unwrappedMasterKey);
              await storeMasterKey(unwrappedMasterKey);
              return { authKey: keys.authKey, masterKey: unwrappedMasterKey };
            }
          }
        } catch (error) {
          console.warn('Failed to fetch wrapped key from server:', error);
        }
      }

      // Fallback: derive master key from password (legacy behavior for migration)
      // This maintains backward compatibility with existing users who haven't migrated
      setMasterKeyState(keys.masterKey);
      await storeMasterKey(keys.masterKey);
      return keys;
    },
    []
  );

  // Set master key directly (for signup or recovery)
  const setMasterKeyDirect = useCallback(async (key: Uint8Array) => {
    setMasterKeyState(key);
    await storeMasterKey(key);
  }, []);

  // Lock: clear keys
  const lockVault = useCallback(async () => {
    setMasterKeyState(null);
    await clearMasterKey();
  }, []);

  // Encrypt function
  const encryptFn = useCallback(
    async (plaintext: string): Promise<string> => {
      if (!masterKey) {
        throw new Error('Vault is locked - cannot encrypt');
      }
      return encrypt(plaintext, masterKey);
    },
    [masterKey]
  );

  // Decrypt function
  const decryptFn = useCallback(
    async (ciphertext: string): Promise<string> => {
      if (!masterKey) {
        throw new Error('Vault is locked - cannot decrypt');
      }
      return decrypt(ciphertext, masterKey);
    },
    [masterKey]
  );

  // Encrypt object function
  const encryptObjectFn = useCallback(
    async <T,>(obj: T): Promise<string> => {
      if (!masterKey) {
        throw new Error('Vault is locked - cannot encrypt');
      }
      return encryptObject(obj, masterKey);
    },
    [masterKey]
  );

  // Decrypt object function
  const decryptObjectFn = useCallback(
    async <T,>(ciphertext: string): Promise<T> => {
      if (!masterKey) {
        throw new Error('Vault is locked - cannot decrypt');
      }
      return decryptObject<T>(ciphertext, masterKey);
    },
    [masterKey]
  );

  // Get master key (for wrapping operations)
  const getMasterKey = useCallback(() => masterKey, [masterKey]);

  const value = useMemo<CryptoContextType>(
    () => ({
      isUnlocked: !!masterKey,
      isLoading,
      unlock: unlockVault,
      setMasterKey: setMasterKeyDirect,
      lock: lockVault,
      encrypt: encryptFn,
      decrypt: decryptFn,
      encryptObject: encryptObjectFn,
      decryptObject: decryptObjectFn,
      getMasterKey,
    }),
    [
      masterKey,
      isLoading,
      unlockVault,
      setMasterKeyDirect,
      lockVault,
      encryptFn,
      decryptFn,
      encryptObjectFn,
      decryptObjectFn,
      getMasterKey,
    ]
  );

  return (
    <CryptoContext.Provider value={value}>{children}</CryptoContext.Provider>
  );
}
